import { useState, useEffect } from "react";
import { Loader2, Save, FileText, ShieldCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const inputCls = "w-full bg-ink-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none";

export default function LegalContactAdmin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-config")
      .then(({ data }) => setForm({
        terms: data.terms || "",
        privacy: data.privacy || "",
        contact_email: data.contact_email || "",
        contact_message: data.contact_message || "",
      }))
      .catch(() => toast.error("Failed to load settings"));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-config", form);
      toast.success("Legal & contact updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!form) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={26} /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-legal-contact">
      <div className="flex items-center justify-between">
        <h2 className="label-mono flex items-center gap-3"><span className="w-8 h-px bg-gold" /> Legal &amp; Contact</h2>
        <button onClick={save} disabled={saving} data-testid="legal-save"
          className="flex items-center gap-2 bg-gold text-ink-950 font-heading font-bold text-sm px-4 py-2 rounded-md hover:bg-goldlight transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
        </button>
      </div>
      <p className="text-xs text-zinc-500 -mt-2">These appear on the user Settings page (Legal &amp; Support sections).</p>

      <div className="rounded-xl border border-white/10 bg-ink-900/60 p-4 space-y-2">
        <div className="label-mono flex items-center gap-2"><FileText size={14} className="text-gold" /> Terms &amp; Conditions</div>
        <textarea value={form.terms} onChange={(e) => upd("terms", e.target.value)} rows={7} data-testid="legal-terms"
          className={`${inputCls} resize-y leading-relaxed`} placeholder="Terms & Conditions text…" />
      </div>

      <div className="rounded-xl border border-white/10 bg-ink-900/60 p-4 space-y-2">
        <div className="label-mono flex items-center gap-2"><ShieldCheck size={14} className="text-gold" /> Privacy Policy</div>
        <textarea value={form.privacy} onChange={(e) => upd("privacy", e.target.value)} rows={7} data-testid="legal-privacy"
          className={`${inputCls} resize-y leading-relaxed`} placeholder="Privacy Policy text…" />
      </div>

      <div className="rounded-xl border border-white/10 bg-ink-900/60 p-4 space-y-3">
        <div className="label-mono flex items-center gap-2"><Mail size={14} className="text-gold" /> Contact</div>
        <div>
          <div className="label-mono mb-1.5">Contact email</div>
          <input value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} data-testid="legal-contact-email"
            className={inputCls} placeholder="support@nosey.ai" type="email" />
        </div>
        <div>
          <div className="label-mono mb-1.5">Contact message (optional)</div>
          <textarea value={form.contact_message} onChange={(e) => upd("contact_message", e.target.value)} rows={3} data-testid="legal-contact-message"
            className={`${inputCls} resize-y`} placeholder="Shown in the Help / Questions dialog…" />
        </div>
      </div>
    </div>
  );
}
